import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBookings } from '../actions/bookingActions';
import BookingCard from '../components/BookingCard';

// GENERATED for Squeakky House of Service from the client's SaaS brief — see
// BUILD-NOTES.md. The copy below is theirs; the bracketed bits are placeholders.

const SERVICES = [
  { icon: "📅", title: "Browse services", body: "View the different cleaning types offered (residential, commercial/office, post-construction) and what each includes." },
  { icon: "🚀", title: "How it reaches you", body: "We deliver in person through vetted cleaning teams based in each city we operate." },
  { icon: "📅", title: "Booking calendar", body: "For selecting date/time and checking availability directly on the site." },
  { icon: "✉️", title: "Email", body: "Booking confirmations, receipts, and follow-ups." },
  { icon: "✅", title: "On-site quality check", body: "Our team reviews the completed job against our service checklist before leaving." },
  { icon: "💬", title: "WhatsApp", body: "Primary channel for quick questions, booking support, and day-of-service updates." },
  { icon: "✅", title: "Customer walkthrough", body: "For larger jobs (post-construction, offices), the customer or their representative does a final walkthrough before sign-off." },
  { icon: "✅", title: "Photos of completed work", body: "Sent via WhatsApp/email for jobs where the customer isn't physically present (e.g. short-term rental turnovers, remote property managers)." },
];

const STEPS = [
  { n: "01", title: "Visits the website", body: "Customer visits the website and selects the type of cleaning they need (residential, office/commercial, or post-construction)." },
  { n: "02", title: "They book online", body: "Choose a date, time, property size/type, and any specific requirements, then confirm the booking." },
  { n: "03", title: "Pays a small deposit", body: "Customer pays a small deposit (30% or 40% to confirm the slot and lock in the booking)." },
];

const TESTIMONIALS = [
  { name: "[First customer]", city: "Business owners", item: "Booking · Clean their properties", quote: "Replace with a real quote once you have one." },
  { name: "[Second customer]", city: "Property managers", item: "Booking · Before → after", quote: "A short, specific before/after beats a generic testimonial." },
  { name: "[Third customer]", city: "Homeowners", item: "Booking · Live in days", quote: "Never borrow anyone else’s outcome — earn one and put it here." },
];

export default function Home() {
  const dispatch = useDispatch();
  const { items } = useSelector(s => s.bookings);
  useEffect(() => { dispatch(fetchBookings({ limit: 6 })); }, [dispatch]);

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <p className="hero-eyebrow">{"House Of Service"}</p>
          <h1>{"We help people clean their properties"}</h1>
          <p className="hero-sub">{"We provide dependable, professional cleaning services — residential, commercial, and post-construction — that customers can book easily and trust to get done right…"}</p>
          <div className="hero-cta">
            <Link to="/inventory" className="btn-primary">{"Book now"}</Link>
            <a href="#services" className="btn-ghost">What we do</a>
          </div>
        </div>
      </section>

      <section className="section demo-banner" aria-label="Demo credentials">
        <div className="demo-banner-inner">
          <div className="demo-banner-tag">DEMO MODE</div>
          <div className="demo-banner-body">
            <p><strong>This is the Squeakky House of Service app, built on the Free Builder kit.</strong> Log in with one of the demo accounts below, then seed demo data from the Admin panel.</p>
            <ul className="demo-creds">
              <li><span className="role admin">Admin</span><code>admin@squeakky.demo</code> / <code>demo1234</code></li>
              <li><span className="role manager">Manager</span><code>manager@squeakky.demo</code> / <code>demo1234</code></li>
              <li><span className="role sales">Staff</span><code>sales@squeakky.demo</code> / <code>demo1234</code></li>
              <li><span className="role customer">Customer</span><code>customer@squeakky.demo</code> / <code>demo1234</code></li>
            </ul>
            <p className="demo-banner-fine">Same passwords. Same database. Roles gate different parts of the UI. <Link to="/docs">Read the docs</Link> to see how each module is wired.</p>
          </div>
        </div>
      </section>

      {true && (
      <section className="section">
        <div className="section-head">
          <h2>Latest bookings</h2>
          <Link to="/inventory">See all →</Link>
        </div>
        <div className="grid grid-3">
          {items.map(x => <BookingCard key={x._id} booking={x} />)}
          {items.length === 0 && <p className="muted">Nothing yet. (Admins: seed demo data from the Admin panel.)</p>}
        </div>
      </section>)}

      {true && (
      <section className="section" id="services">
        <div className="section-head"><h2>What we do for you</h2></div>
        <p className="section-lead">{"We provide dependable, professional cleaning services — residential, commercial, and post-construction — that customers can book easily and trust to get done right…"}</p>
        <div className="grid grid-4 services-grid">
          {SERVICES.map(s => (
            <div key={s.title} className="service-card">
              <span className="service-icon" aria-hidden="true">{s.icon}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </section>)}

      {true && (
      <section className="section" id="how-it-works">
        <div className="section-head"><h2>How it works</h2></div>
        <div className="grid grid-3">
          {STEPS.map(s => (
            <div key={s.n} className="step"><span className="step-num">{s.n}</span><h3>{s.title}</h3><p>{s.body}</p></div>
          ))}
        </div>
      </section>)}

      {true && (
      <section className="section" id="testimonials">
        <div className="section-head"><h2>What customers say</h2></div>
        <div className="grid grid-3">
          {TESTIMONIALS.map(t => (
            <figure key={t.name} className="testimonial">
              <blockquote>"{t.quote}"</blockquote>
              <figcaption>
                <strong>{t.name}</strong>
                <span className="muted"> · {t.city}</span>
                <div className="testimonial-car">{t.item}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>)}

      <section className="section cta-band" id="visit">
        <div className="cta-band-inner">
          <div>
            <h2>{"Ready for your first booking?"}</h2>
            <p>{"WhatsApp — primary channel for quick questions, booking support, and day-of-service updates."}</p>
          </div>
          <div className="cta-band-actions">
            <Link to="/inventory" className="btn-primary">{"Book now"}</Link>
            <Link to="/questions" className="btn-ghost">Ask a question</Link>
          </div>
        </div>
      </section>
    </>
  );
}
